import { useState } from "react";

// Third party
import { AnimatePresence, motion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";

// Components
import AuthenticationForm from "./components/AuthenticationForm";
import AuthenticationLink from "./components/AuthenticationLink";

// API
import { getRequestClientAccess, TGetProviderList } from "@/Settings/api/AdministrationTools/queries";

// Types
import { IRequestClientAccessResponse } from "@/Settings/types/administrationTools/utils";
import { IIntegrationProvider, IConfiguredAccount } from "@/Settings/types/administrationTools/model";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Constants
const MotionDialogContent = motion.create(DialogContent);

const QUERY_KEY_REQUEST_CLIENT_ACCESS = "payroll-provider-request-client-access";

const REQUEST_CLIENT_ACCESS_ERROR = {
	hasError: true,
	message: "Invalid Display ID. Please try again.",
};

const isDisplayIdAlreadyConfiguredAndConnected = (configuredAccounts: IConfiguredAccount[], displayId: string) => {
	return configuredAccounts.some((account) => account.displayId === displayId && account.status === "CONNECTED");
};

interface IAuthenticatorDialogProps {
	locationId: string | undefined;
	isOpen: boolean;
	onClose: () => void;
	selectedProvider: TGetProviderList | undefined;
	payrollProvider: IIntegrationProvider | undefined;
}

const AuthenticatorDialog = (props: IAuthenticatorDialogProps) => {
	const { locationId, isOpen, onClose, selectedProvider, payrollProvider } = props;
	const queryClient = useQueryClient();
	const [displayId, setDisplayId] = useState<string>("");
	const [approvalLinkInError, setApprovalLinkInError] = useState({
		hasError: false,
		message: "",
	});

	/***************************************** QUERIES ****************************************/

	const {
		data: requestLink,
		isFetching,
		refetch: requestLinkRefetch,
	} = useQuery<IRequestClientAccessResponse | null>({
		queryKey: [QUERY_KEY_REQUEST_CLIENT_ACCESS, selectedProvider?.providerName, displayId],
		queryFn: async () => {
			if (!locationId || !selectedProvider?.providerName || !displayId) {
				return null;
			}
			const response = await getRequestClientAccess(locationId, selectedProvider.providerName, displayId);
			if (!response?.approvalLink) {
				setApprovalLinkInError({
					hasError: true,
					message: response?.errorMessage ?? REQUEST_CLIENT_ACCESS_ERROR.message,
				});
				return null;
			}
			return response;
		},
		enabled: false,
		staleTime: 0,
	});

	/***************************************** HANDLERS ****************************************/
	const handleOnCloseDialog = () => {
		queryClient.removeQueries({ queryKey: [QUERY_KEY_REQUEST_CLIENT_ACCESS] });
		setDisplayId("");
		setApprovalLinkInError({ hasError: false, message: "" });
		onClose();
	};

	const handleChangeDisplayId = (value: string) => {
		setDisplayId(value);

		if (!value || !payrollProvider?.configuredAccounts) {
			setApprovalLinkInError({ hasError: false, message: "" });
			return;
		}

		const isAlreadyConfiguredAndConnected = isDisplayIdAlreadyConfiguredAndConnected(payrollProvider?.configuredAccounts, value);
		if (isAlreadyConfiguredAndConnected) {
			setApprovalLinkInError({
				hasError: true,
				message: "This Company Display ID is already configured and connected.",
			});
			return;
		}
		setApprovalLinkInError({ hasError: false, message: "" });
	};

	const handleRequestLink = () => {
		if (!displayId || isFetching) {
			return;
		}
		requestLinkRefetch();
	};

	return (
		<Dialog
			classes={{
				paper: "!rounded-[10px] !shadow-md p-4",
			}}
			open={isOpen}
			onClose={handleOnCloseDialog}
			PaperProps={{
				style: {
					width: "344px",
				},
			}}
			disableEscapeKeyDown
			onDoubleClick={(e) => {
				e.stopPropagation();
			}}
		>
			<DialogTitle>
				<IconButton className="!absolute !right-1 !top-1 !text-default" aria-label="close" onClick={handleOnCloseDialog}>
					<CloseRoundedIcon fontSize="medium" />
				</IconButton>
				<Stack direction={"column"} alignItems={"center"} spacing={1}>
					<Typography className={"!font-montserrat !text-lg !font-medium !text-default"}>Authenticate</Typography>
					<img src={selectedProvider?.imageUrl} alt={selectedProvider?.provider || "Provider Logo"} style={{ height: 32 }} />
				</Stack>
			</DialogTitle>
			<MotionDialogContent sx={{ overflow: "hidden" }} animate={{ height: "auto" }} transition={{ duration: 0.5 }}>
				<AnimatePresence mode="wait">
					{!requestLink ? (
						<motion.div key="form" exit={{ opacity: 0, x: -300 }} transition={{ duration: 0.3 }}>
							<AuthenticationForm
								displayId={displayId}
								isFetching={isFetching}
								approvalLinkInError={approvalLinkInError}
								payrollProvider={payrollProvider}
								handleChangeDisplayId={handleChangeDisplayId}
								handleRequestLink={handleRequestLink}
							/>
						</motion.div>
					) : (
						<motion.div
							key="link"
							initial={{ opacity: 0, x: 300 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.3 }}
						>
							<AuthenticationLink requestLink={requestLink} locationId={locationId} />
						</motion.div>
					)}
				</AnimatePresence>
			</MotionDialogContent>
		</Dialog>
	);
};

export default AuthenticatorDialog;
